import { Eyebrow } from '@workspace/ui/components/eyebrow'
import { type SurfaceTone, toneBorder, toneText } from '@workspace/ui/lib/tone'
import { cn } from '@workspace/ui/lib/utils'
import type { ReactNode } from 'react'

export interface FocusCardProps {
  eyebrow?: ReactNode
  /**
   * Headline, split so exactly one phrase carries the accent colour. Passing
   * the whole sentence as `title` and leaving `titleAccent` unset is fine; the
   * split exists so the accent is a prop rather than markup inside a string.
   */
  title: ReactNode
  titleAccent?: ReactNode
  /** Supporting sentence under the headline. */
  lede?: ReactNode
  /** The card's own content — a visual, a list, a chooser. */
  children?: ReactNode
  /** Primary and secondary actions. */
  actions?: ReactNode
  /** Quiet line under the actions, e.g. a pricing or evidence note. */
  footnote?: ReactNode
  /**
   * `alert` turns the eyebrow, the accent phrase and the border red. Buttons
   * stay the brand colour: red is reserved for destructive actions, so an
   * alert card must not make its way forward look dangerous.
   */
  tone?: SurfaceTone
  /**
   * Announce content changes. The Home wizard re-resolves on a timer, so the
   * card can change with no user action and a screen reader would otherwise
   * never learn that the wait ended.
   */
  live?: boolean
  className?: string
}

/**
 * One focused thing to read and one thing to do: eyebrow, accented headline,
 * supporting line, content, actions.
 *
 * Extracted because this arrangement was hand-rolled four times over (the
 * outreach acquisition hero, the improve-score CTA, the dashboard's no-deck
 * hero and the launch panel) with the spacing drifting between them.
 */
export function FocusCard({
  eyebrow,
  title,
  titleAccent,
  lede,
  children,
  actions,
  footnote,
  tone = 'default',
  live = false,
  className,
}: FocusCardProps) {
  return (
    <section
      data-slot="focus-card"
      aria-live={live ? 'polite' : undefined}
      className={cn(
        'rounded-2xl border bg-gradient-to-br from-card/40 to-card/10 px-7 py-8',
        toneBorder(tone),
        className
      )}
    >
      {eyebrow && <Eyebrow tone={tone}>{eyebrow}</Eyebrow>}

      <h1 className="mt-2 max-w-2xl text-balance text-[30px] font-bold leading-[1.1] tracking-[-0.03em] text-foreground sm:text-[34px]">
        {title}
        {titleAccent && (
          <>
            {' '}
            <span className={toneText(tone)}>{titleAccent}</span>
          </>
        )}
      </h1>

      {lede && (
        <p className="mt-3 max-w-2xl text-[14.5px] leading-relaxed text-foreground/80">{lede}</p>
      )}

      {children && <div className="mt-6">{children}</div>}

      {actions && <div className="mt-6 flex flex-wrap items-center gap-3">{actions}</div>}

      {footnote && <div className="mt-3.5 text-[12px] text-muted-foreground">{footnote}</div>}
    </section>
  )
}
